import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import WarningIcon from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';

interface SessionTimeoutDialogProps {
  open: boolean;
  onExtendSession: () => void;
  onLogout: () => void;
  /** Called when the countdown reaches zero. Defaults to onLogout if not provided. */
  onCountdownExpired?: () => void;
  remainingSeconds: number;
}

export const SessionTimeoutDialog: React.FC<SessionTimeoutDialogProps> = ({
  open,
  onExtendSession,
  onLogout,
  onCountdownExpired,
  remainingSeconds,
}) => {
  const [countdown, setCountdown] = useState(remainingSeconds);

  useEffect(() => {
    setCountdown(remainingSeconds);
  }, [remainingSeconds]);

  // When countdown reaches zero, fire onCountdownExpired (sign out) or fall back to onLogout
  const handleExpired = onCountdownExpired ?? onLogout;

  useEffect(() => {
    if (!open) return;

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          handleExpired();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open, handleExpired]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progressValue = (countdown / remainingSeconds) * 100;

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderTop: 3, borderColor: 'warning.main' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon color="warning" />
        Session Timeout Warning
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Your session will expire due to inactivity. You will be automatically 
          logged out in:
        </Typography>
        
        <Box sx={{ my: 3, textAlign: 'center' }}>
          <Typography variant="h4" color="warning.main" gutterBottom>
            {formatTime(countdown)}
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={progressValue} 
            color="warning"
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
        
        <Typography variant="body2" color="text.secondary">
          Click "Stay Signed In" to extend your session, or "Sign Out" to log out now.
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={onLogout} 
          color="inherit"
          variant="outlined"
        >
          Sign Out
        </Button>
        <Button 
          onClick={onExtendSession} 
          color="primary"
          variant="contained"
          autoFocus
        >
          Stay Signed In
        </Button>
      </DialogActions>
    </Dialog>
  );
};